import http from '@/http'
import type { ApiId, ApiResponse } from '@/types'
import { toApiId } from '@/common/utils/id'

export interface HelpAnchor {
  id: string
  text: string
  level: number
}

export interface HelpCategory {
  id: ApiId
  name: string
  type: 'tutorial' | 'faq' | string
  parentId?: ApiId | null
  sort?: number
  enabled?: number
  children?: HelpCategory[]
  createTime?: string
  updateTime?: string
}

export interface HelpArticle {
  id: ApiId
  categoryId: ApiId
  categoryName?: string
  categoryType?: string
  title: string
  summary?: string
  contentHtml?: string
  keywords?: string
  helpKey?: string
  coverUrl?: string
  sort?: number
  enabled?: number
  viewCount?: number
  anchors?: HelpAnchor[]
  createTime?: string
  updateTime?: string
}

export interface HelpArticleListParams {
  page?: number
  perPage?: number
  categoryId?: ApiId
  keyword?: string
  enabled?: number
  type?: string
}

function buildListBody(params?: HelpArticleListParams) {
  const body: Record<string, unknown> = {
    pageNo: params?.page || 1,
    pageSize: params?.perPage || 20
  }
  if (params?.categoryId != null && params.categoryId !== '') body.categoryId = toApiId(params.categoryId)
  if (params?.keyword) body.keyword = params.keyword
  if (params?.enabled !== undefined) body.enabled = params.enabled
  if (params?.type) body.type = params.type
  return body
}

/** 用户侧 */
export function getHelpCategoryTree() {
  return http.get<ApiResponse<HelpCategory[]>>('/help/categories/tree')
}

export function getHelpArticleList(params?: HelpArticleListParams) {
  return http.post<ApiResponse<{
    total: number
    pages: number
    list: HelpArticle[]
  }>>('/help/articles/list', buildListBody(params))
}

export function getHelpArticle(id: ApiId) {
  return http.get<ApiResponse<HelpArticle>>(`/help/articles/${toApiId(id)}`)
}

export function getHelpArticleByKey(helpKey: string) {
  return http.get<ApiResponse<HelpArticle>>(`/help/by-key/${encodeURIComponent(helpKey)}`)
}

/** 管理侧 */
export function getManageHelpCategoryTree() {
  return http.get<ApiResponse<HelpCategory[]>>('/help/manage/categories/tree')
}

export function addHelpCategory(data: {
  name: string
  type: string
  parentId?: ApiId | null
  sort?: number
  enabled?: number
}) {
  return http.post<ApiResponse<void>>('/help/manage/categories/add', {
    ...data,
    parentId: data.parentId != null && data.parentId !== '' ? toApiId(data.parentId) : undefined
  })
}

export function updateHelpCategory(data: {
  id: ApiId
  name?: string
  type?: string
  parentId?: ApiId | null
  sort?: number
  enabled?: number
}) {
  const body: Record<string, unknown> = {
    ...data,
    id: toApiId(data.id)
  }
  if (data.parentId !== undefined) {
    body.parentId = data.parentId != null && data.parentId !== '' ? toApiId(data.parentId) : null
  } else {
    delete body.parentId
  }
  return http.post<ApiResponse<void>>('/help/manage/categories/update', body)
}

export function deleteHelpCategory(id: ApiId) {
  return http.get<ApiResponse<void>>(`/help/manage/categories/delete/${toApiId(id)}`)
}

export function getManageHelpArticleList(params?: HelpArticleListParams) {
  return http.post<ApiResponse<{
    total: number
    pages: number
    list: HelpArticle[]
  }>>('/help/manage/articles/list', buildListBody(params))
}

export function getManageHelpArticle(id: ApiId) {
  return http.get<ApiResponse<HelpArticle>>(`/help/manage/articles/${toApiId(id)}`)
}

export function addHelpArticle(data: {
  categoryId: ApiId
  title: string
  summary?: string
  contentHtml: string
  keywords?: string
  helpKey?: string
  coverUrl?: string
  sort?: number
  enabled?: number
}) {
  return http.post<ApiResponse<void>>('/help/manage/articles/add', {
    ...data,
    categoryId: toApiId(data.categoryId)
  })
}

export function updateHelpArticle(data: {
  id: ApiId
  categoryId?: ApiId
  title?: string
  summary?: string
  contentHtml?: string
  keywords?: string
  helpKey?: string
  coverUrl?: string
  sort?: number
  enabled?: number
}) {
  return http.post<ApiResponse<void>>('/help/manage/articles/update', {
    ...data,
    id: toApiId(data.id),
    categoryId: data.categoryId != null ? toApiId(data.categoryId) : undefined
  })
}

export function deleteHelpArticle(id: ApiId) {
  return http.get<ApiResponse<void>>(`/help/manage/articles/delete/${toApiId(id)}`)
}
